// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useEffect, useState, useCallback, useRef } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { StatusBadge } from '../../../../components/StatusBadge';
import ViewButtonComponent from "../../../../components/ViewButtonComponent";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Card, CardBody, Col, Row, Spinner, Alert, Form } from "react-bootstrap";
import { RequestDetailView } from "../RequestDetailView";
import { OrderRequest, useOrderRequests } from "../../../../hooks/useOrderRequest";
import { DeptBadge } from "../../../../components/DeptBadge";

// Define the props interface
interface RequestTableProps {
  search: string;
  filterStatus: string;
  filterDept: string;
  filterType: string;
  filterDate?: string;
  onSearchChange: (value: string) => void;
  onFilterStatusChange: (value: string) => void;
  onFilterDeptChange: (value: string) => void;
  onFilterTypeChange: (value: string) => void;
  onFilterDateChange?: (date: string) => void;
  onViewRequest: (request: OrderRequest) => void;
}

export const RequestTable: React.FC<RequestTableProps> = ({ 
  search, 
  filterStatus, 
  filterDept, 
  filterType,
  filterDate = new Date().toISOString().split('T')[0], // Default to current date
  onSearchChange,
  onFilterStatusChange,
  onFilterDeptChange,
  onFilterTypeChange,
  onFilterDateChange,
  onViewRequest 
}) => {
  const [localSearch, setLocalSearch] = useState(search);
  const [localFilterDate, setLocalFilterDate] = useState(filterDate);
  const [selectedRequest, setSelectedRequest] = useState<OrderRequest | null>(null);
  const [showDetailView, setShowDetailView] = useState(false);
  
  // Build filters object
  const buildFilters = useCallback(() => {
    const filters: any = {};
    if (filterStatus !== "All Status") filters.status = filterStatus;
    if (filterDept !== "All Departments") filters.department = filterDept;
    if (filterType !== "All Types") filters.requestType = filterType;
    if (localFilterDate) {
      filters.date = localFilterDate;
    }
    return filters;
  }, [filterStatus, filterDept, filterType, localFilterDate]);
  
  // Use the custom hook to fetch real data from Firebase
  const {
    requests: firebaseRequests,
    loading,
    error,
    // statistics,
    // setFilters,
    // approveRequest,
    // rejectRequest
  } = useOrderRequests({
    realtime: true,
    filters: buildFilters()
  });
  
  // Apply search filter locally
  const [filteredRequests, setFilteredRequests] = useState<OrderRequest[]>([]);
  
  // Helper function to check if a date matches the filter date
  const isDateMatching = useCallback((dateValue: any, filterDateStr: string): boolean => {
    if (!filterDateStr) return true;
    
    try {
      let requestDate;
      if (dateValue?.toDate) {
        requestDate = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        requestDate = dateValue;
      } else if (dateValue) {
        requestDate = new Date(dateValue);
      } else {
        return true;
      }
      
      const filterDate = new Date(filterDateStr);
      filterDate.setHours(0, 0, 0, 0);
      
      const requestDateOnly = new Date(requestDate);
      requestDateOnly.setHours(0, 0, 0, 0);
      
      return requestDateOnly.getTime() === filterDate.getTime();
    } catch (error) {
      console.error("Date parsing error:", error);
      return true;
    }
  }, []);
  
  // Apply all filters
  useEffect(() => {
    let filtered = firebaseRequests;
    
    // Apply search filter
    if (localSearch) {
      filtered = filtered.filter(r =>
        r.id.toLowerCase().includes(localSearch.toLowerCase()) || 
        r.requestedBy.toLowerCase().includes(localSearch.toLowerCase()) ||
        (r.products?.[0]?.displayName || "").toLowerCase().includes(localSearch.toLowerCase()) ||
        r.requestType.toLowerCase().includes(localSearch.toLowerCase())
      );
    }
    
    // Apply date filter
    if (localFilterDate) {
      filtered = filtered.filter(r => 
        isDateMatching(r.createdAt || r.targetDate, localFilterDate)
      );
    }
    
    setFilteredRequests(filtered);
  }, [firebaseRequests, localSearch, localFilterDate, isDateMatching]);
  
  // Update parent search state
  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
    onSearchChange(value);
  };
  
  // Handle date change
  const handleDateChange = (value: string) => {
    setLocalFilterDate(value);
    if (onFilterDateChange) {
      onFilterDateChange(value);
    }
  };
  
  // Handle clear date filter (set to current date)
  // const handleClearDateFilter = () => {
  //   const currentDate = new Date().toISOString().split('T')[0];
  //   setLocalFilterDate(currentDate);
  //   if (onFilterDateChange) {
  //     onFilterDateChange(currentDate);
  //   }
  // };
  
  // Handle view request
  const handleViewRequest = (request: OrderRequest) => {
    setSelectedRequest(request);
    setShowDetailView(true);
    if (onViewRequest) {
      onViewRequest(request);
    }
  };
  
  // Format date for better display
  const formatDate = (dateValue: any): string => {
    if (!dateValue) return 'N/A';
    try {
      let date;
      if (dateValue?.toDate) {
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else {
        date = new Date(dateValue);
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };
  
  // Format amount in LKR (Sri Lankan Rupees)
  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount).replace('LKR', 'Rs.');
  };
  
  // Debug: Log when data is loaded
  useEffect(() => {
    if (!loading && firebaseRequests.length > 0) {
      console.log("✅ Data loaded:", firebaseRequests.length, "requests");
      console.log("Sample request:", firebaseRequests[0]);
    }
  }, [loading, firebaseRequests]);
  
  // Table columns configuration
  const columns: TableColumn<OrderRequest>[] = [
    {
      name: <h6 className="fw-bold mb-0">Request ID</h6>,
      selector: (row) => row.id,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <span className="fw-semibold" style={{ fontSize: "12px" }}>
          {row.id.slice(0, 8)}...
        </span>
      )
    },
    {
      name: <h6 className="fw-bold mb-0">Type</h6>,
      selector: (row) => row.requestType,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <span className="badge bg-info text-dark">
            {row.requestType || 'N/A'}
          </span>
        </div>
      )
    },
    {
      name: <h6 className="fw-bold mb-0">Department</h6>,
      selector: (row) => row.department,
      sortable: true,
      cell: (row) => <DeptBadge dept={row.department || 'N/A'} />
    },
    {
      name: <h6 className="fw-bold mb-0">Requester</h6>,
      selector: (row) => row.requestedBy,
      sortable: true,
      cell: (row) => <span className="fw-medium">{row.requestedBy}</span>
    },
    {
      name: <h6 className="fw-bold mb-0">Products</h6>,
      selector: (row) => row.totalItems,
      sortable: true,
      cell: (row) => (
        <div>
          <span className="fw-semibold">{row.totalItems || 0} items</span>
          <small className="d-block text-muted">{row.totalQuantity || 0} qty</small>
        </div>
      )
    },
    {
      name: <h6 className="fw-bold mb-0">Amount</h6>,
      selector: (row) => row.totalAmount,
      sortable: true,
      cell: (row) => (
        <span className="fw-bold text-primary">
          {formatAmount(row.totalAmount || 0)}
        </span>
      )
    },
    {
      name: <h6 className="fw-bold mb-0">Target Date</h6>,
      selector: (row) => row.targetDate?.toString() || '',
      sortable: true,
      cell: (row) => formatDate(row.targetDate)
    },
    {
      name: <h6 className="fw-bold mb-0">Created Date</h6>,
      selector: (row) => row.createdAt?.toString() || '',
      sortable: true,
      cell: (row) => formatDate(row.createdAt)
    },
    {
      name: <h6 className="fw-bold mb-0">Status</h6>,
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => <StatusBadge status={row.status} />
    },
    {
      name: <h6 className="fw-bold mb-0">Actions</h6>,
      cell: (row) => (
        <ViewButtonComponent onClick={() => handleViewRequest(row)} />
      ),
      width: "100px",
    },
  ];
  
  // Filter options
  const statusOptions = ["All Status", "Pending", "Approved", "Rejected"];
  const deptOptions = ["All Departments", "Sales & Marketing", "Distribution", "Ash"];
  const typeOptions = ["All Types", "Product Quantity", "Cart Allocation", "Special Event"];
  
  // Loading state
  if (loading && firebaseRequests.length === 0) {
    return (
      <Card className="mt-1">
        <CardBody className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading requests...</p>
        </CardBody>
      </Card>
    );
  }
  
  // Error state
  if (error) {
    return (
      <Card className="mt-1">
        <CardBody>
          <Alert variant="danger">
            <Alert.Heading>Error Loading Requests</Alert.Heading>
            <p>{error.message}</p>
          </Alert>
        </CardBody>
      </Card>
    );
  }
  
  return (
    <>
      <Card className="mt-1">
        <CardBody>
          {/* Filter Section */}
          <Row className="mb-3 g-3">
            <Col md={3}>
              <label className="form-label fw-semibold">Search</label>
              <input 
                type="text"
                className="form-control"
                placeholder="Search by ID, requester, product..."
                value={localSearch}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <label className="form-label fw-semibold">Status</label>
              <select 
                className="form-select"
                value={filterStatus} 
                onChange={(e) => onFilterStatusChange(e.target.value)}
              >
                {statusOptions.map(o => <option key={o}>{o}</option>)}
              </select>
            </Col>
            <Col md={2}>
              <label className="form-label fw-semibold">Department</label>
              <select 
                className="form-select"
                value={filterDept} 
                onChange={(e) => onFilterDeptChange(e.target.value)}
              >
                {deptOptions.map(o => <option key={o}>{o}</option>)}
              </select>
            </Col>
            <Col md={2}>
              <label className="form-label fw-semibold">Request Type</label>
              <select 
                className="form-select"
                value={filterType} 
                onChange={(e) => onFilterTypeChange(e.target.value)}
              >
                {typeOptions.map(o => <option key={o}>{o}</option>)}
              </select>
            </Col>
            <Col md={3}>
              <label className="form-label fw-semibold">Filter by Date</label>
              <div className="d-flex gap-2">
                <input 
                  type="date"
                  className="form-control"
                  value={localFilterDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                />
               
              </div>
            </Col>
          </Row>
          
      
          
          {/* Statistics Summary */}
          {filteredRequests.length > 0 && (
            <Row className="mb-3">
              <Col>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <span className="text-muted">Showing </span>
                    <strong>{filteredRequests.length}</strong>
                    <span className="text-muted"> of {firebaseRequests.length} requests</span>
                  </div>
                  <div className="d-flex gap-3">
                    <div>
                      <small className="text-muted">Total Amount:</small>
                      <strong className="ms-1 text-primary">
                        {formatAmount(filteredRequests.reduce((sum, r) => sum + (r.totalAmount || 0), 0))}
                      </strong>
                    </div>
                    <div>
                      <small className="text-muted">Total Items:</small>
                      <strong className="ms-1">
                        {filteredRequests.reduce((sum, r) => sum + (r.totalItems || 0), 0)}
                      </strong>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          )}
          
          {/* Data Table */}
          {filteredRequests.length === 0 && !loading ? (
            <div className="text-center p-4">
              <i className="bi bi-inbox fs-1 text-muted"></i>
              <p className="mt-2">
                No requests found for {localFilterDate ? new Date(localFilterDate).toLocaleDateString() : 'selected date'}
              </p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredRequests}
              responsive
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              highlightOnHover
              striped
              noDataComponent={
                <div className="text-center p-4">
                  <i className="bi bi-inbox fs-1 text-muted"></i>
                  <p className="mt-2">No requests found</p>
                </div>
              }
              customStyles={{
                headRow: {
                  style: {
                    backgroundColor: '#f8f9fa',
                    borderBottomWidth: '2px',
                    borderBottomColor: '#dee2e6',
                  },
                },
                headCells: {
                  style: {
                    fontSize: '14px',
                    fontWeight: 'bold',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                  },
                },
                rows: {
                  style: {
                    minHeight: '52px',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  },
                },
              }}
            />
          )}
        </CardBody>
      </Card>
      
      {/* Detail View Modal */}
      {showDetailView && selectedRequest && (
        <RequestDetailView 
          request={selectedRequest}
          onClose={() => setShowDetailView(false)}
          products={selectedRequest.products || []}
        />
      )}
    </>
  );
};